import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext('light');

export function useTheme() {
  return useContext(ThemeContext);
}

const THEME_KEY = 'ozstaff_theme';
const LEGACY_THEME_KEY = 'socioniq_theme';

function getSystemTheme() {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredTheme() {
  try {
    const canonical = localStorage.getItem(THEME_KEY);
    if (canonical === 'light' || canonical === 'dark') return canonical;
    const legacy = localStorage.getItem(LEGACY_THEME_KEY);
    if (legacy === 'light' || legacy === 'dark') {
      localStorage.setItem(THEME_KEY, legacy); // one-time migration
      return legacy;
    }
  } catch (_) {}
  return null;
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    return getStoredTheme() || getSystemTheme();
  });

  // Listen for system theme changes (only when no user preference is set)
  useEffect(() => {
    const stored = getStoredTheme();
    if (stored) return;
    const handler = (e) => setTheme(e.matches ? 'dark' : 'light');
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', handler);
    return () => window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Sync when landing or another tab changes the theme (canonical + legacy)
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== THEME_KEY && e.key !== LEGACY_THEME_KEY) return;
      if (e.newValue === 'light' || e.newValue === 'dark') setTheme(e.newValue);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    try {
      localStorage.setItem(THEME_KEY, next);
      localStorage.setItem(LEGACY_THEME_KEY, next);
    } catch (_) {}
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
